import StarRoundedIcon from "@mui/icons-material/StarRounded";
import type { KeyboardEvent, MouseEvent, ReactNode } from "react";
import { Box, Stack, Typography } from "~/common/mui";
import { ControlInfo } from "./ControlInfo";
import type { ControlOptionViewModel } from "./controlViewModel";

interface ControlOptionLabelProps {
  option: ControlOptionViewModel;
  standardLabel?: string;
  emphasis?: boolean;
}

const hasInfoContent = (content: ReactNode): boolean => {
  if (content == null || typeof content === "boolean") {
    return false;
  }
  if (typeof content === "string") {
    return content.trim().length > 0;
  }
  if (Array.isArray(content)) {
    return content.some(hasInfoContent);
  }
  return true;
};

const stopOptionInteraction = (event: MouseEvent<HTMLElement> | KeyboardEvent<HTMLElement>) => {
  event.stopPropagation();
};

export const ControlOptionLabel = ({ option, standardLabel, emphasis = false }: ControlOptionLabelProps) => (
  <Stack direction="row" gap={1} alignItems="center" sx={{ minWidth: 0 }}>
    {option.standard ? (
      <StarRoundedIcon color="primary" sx={(theme) => ({ flexShrink: 0, fontSize: theme.spacing(2) })} />
    ) : null}
    <Typography
      variant={emphasis || option.description ? "bodyLarge" : "bodyMedium"}
      sx={{ flex: 1, minWidth: 0, fontWeight: emphasis ? 700 : undefined }}
    >
      {option.label}
      {option.standard && standardLabel ? ` (${standardLabel})` : ""}
    </Typography>
    {option.info && hasInfoContent(option.info.content) ? (
      <Box
        component="span"
        sx={{ display: "inline-flex", flexShrink: 0 }}
        onClick={stopOptionInteraction}
        onKeyDown={stopOptionInteraction}
        onKeyUp={stopOptionInteraction}
      >
        <ControlInfo {...option.info} presentation={option.info.presentation ?? "dialog"} />
      </Box>
    ) : null}
  </Stack>
);
